import { Message } from '@docs/kd-d2c-components';
import { MoreOutlined } from '@ant-design/icons';
import { Button, Dropdown, Tag } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  SchedulerCardAction,
  SchedulerTaskCardStatus,
  SchedulerTaskCardPayload,
} from '@src/types/scheduler-message';
import { decodeSchedulerPayload } from '@src/utils/scheduler-message';
import { marioSchedulerDetail } from '@src/services/walle';
import {
  executeSchedulerAction,
  isAllowedSchedulerAction,
} from '@src/utils/scheduler-action';
import { formatSchedulerFirstRunTime } from '@src/utils/scheduler-time';
import { emitConversationEventsRefresh } from '@src/utils/conversation-events-refresh';
import { EventBus } from '@src/utils/event-bus';
import { GlobalEventType } from '@src/types';
import SchedulerIcon from '@src/assets/images/scheduler-card-icon.png';
import './index.less';

interface Props {
  payload?: string;
}

interface SchedulerTaskDetailData {
  config_id?: string;
  conversation_id?: string;
  title?: string;
  task?: string;
  trigger_description?: string;
  next_run_time?: number | string | null;
  enabled?: boolean;
  trigger_config?: SchedulerTaskCardPayload['trigger_config'];
  expire_time?: number | string | Date | null;
}

interface SchedulerTaskDetailResponse {
  code?: number;
  status?: number | string;
  message?: string;
  data?: SchedulerTaskDetailData;
}

const detailRequestMap = new Map<string, Promise<SchedulerTaskDetailData>>();

const toTimestampMs = (
  value?: number | string | Date | null,
): number | null | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }
  const text = value.trim();
  if (!text) {
    return null;
  }
  if (/^\d{13}$/.test(text)) {
    return Number(text);
  }
  if (/^\d{10}$/.test(text)) {
    return Number(text) * 1000;
  }
  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeTaskCardStatus = (
  status?: SchedulerTaskCardStatus,
): SchedulerTaskCardStatus => {
  if (status === SchedulerTaskCardStatus.PAUSED) {
    return SchedulerTaskCardStatus.PAUSED;
  }
  if (status === SchedulerTaskCardStatus.DELETED) {
    return SchedulerTaskCardStatus.DELETED;
  }
  return SchedulerTaskCardStatus.RUNNING;
};

const normalizeStatusByDetail = (
  snapshotStatus?: SchedulerTaskCardStatus,
  enabled?: boolean,
) => {
  if (snapshotStatus === SchedulerTaskCardStatus.DELETED) {
    return SchedulerTaskCardStatus.DELETED;
  }
  if (typeof enabled === 'boolean') {
    return enabled
      ? SchedulerTaskCardStatus.RUNNING
      : SchedulerTaskCardStatus.PAUSED;
  }
  return normalizeTaskCardStatus(snapshotStatus);
};

const fetchSchedulerTaskDetail = async (configId: string) => {
  const cachedRequest = detailRequestMap.get(configId);
  if (cachedRequest) {
    return cachedRequest;
  }

  const request = marioSchedulerDetail({ config_id: configId })
    .then((response) => {
      const data = response as SchedulerTaskDetailResponse;
      const isSuccess =
        data?.code === 0 || data?.status === 0 || data?.status === 'success';
      if (!isSuccess || !data?.data) {
        throw new Error(data?.message || '同步任务详情失败');
      }
      return data.data;
    })
    .finally(() => {
      detailRequestMap.delete(configId);
    });

  detailRequestMap.set(configId, request);
  return request;
};

export const SchedulerTaskCard = ({ payload }: Props) => {
  const snapshotPayload = useMemo(
    () =>
      decodeSchedulerPayload<SchedulerTaskCardPayload>(payload) ||
      ({} as SchedulerTaskCardPayload),
    [payload],
  );
  const [viewPayload, setViewPayload] =
    useState<SchedulerTaskCardPayload>(snapshotPayload);
  const [loadingType, setLoadingType] = useState('');
  const [syncing, setSyncing] = useState(false);
  const { conversationId = '' } = useParams<{ conversationId?: string }>();
  const configId = viewPayload?.config_id || snapshotPayload?.config_id || '';
  const triggerType = viewPayload?.trigger_config?.trigger_type;
  const [cardStatus, setCardStatus] = useState<SchedulerTaskCardStatus>(
    normalizeTaskCardStatus(snapshotPayload?.status),
  );

  useEffect(() => {
    setViewPayload(snapshotPayload);
    setCardStatus(normalizeTaskCardStatus(snapshotPayload?.status));
  }, [snapshotPayload]);

  const syncTaskDetail = useCallback(async () => {
    if (!configId) {
      return;
    }

    setSyncing(true);
    try {
      const latest = await fetchSchedulerTaskDetail(configId);
      setViewPayload((prev) => ({
        ...prev,
        config_id: latest.config_id || prev.config_id || configId,
        conversation_id: latest.conversation_id ?? prev.conversation_id,
        title: latest.title ?? prev.title,
        task: latest.task ?? prev.task,
        trigger_description:
          latest.trigger_description ?? prev.trigger_description,
        next_run_time:
          toTimestampMs(latest.next_run_time) ?? prev.next_run_time,
        trigger_config: latest.trigger_config ?? prev.trigger_config,
        expire_time: toTimestampMs(latest.expire_time) ?? prev.expire_time,
      }));
      setCardStatus(
        normalizeStatusByDetail(snapshotPayload?.status, latest.enabled),
      );
    } catch (_error) {
      return;
    } finally {
      setSyncing(false);
    }
  }, [configId, snapshotPayload?.status]);

  useEffect(() => {
    syncTaskDetail();
  }, [syncTaskDetail]);

  const cardStatusLabel = useMemo(() => {
    if (cardStatus === SchedulerTaskCardStatus.RUNNING) {
      return '';
    }
    if (cardStatus === SchedulerTaskCardStatus.PAUSED) {
      return '已暂停';
    }
    if (cardStatus === SchedulerTaskCardStatus.DELETED) {
      return '已删除';
    }
    return '';
  }, [cardStatus]);

  const runAction = useMemo(
    () => ({
      type: 'run',
      label: '运行',
      api: '/api/mario/scheduler/run',
      method: 'POST',
      params: { config_id: configId },
    }),
    [configId],
  );

  const moreActions = useMemo(() => {
    const editAction = {
      type: 'edit',
      label: '编辑任务',
      api: '',
    } as SchedulerCardAction;
    const viewAllAction = {
      type: 'view_all',
      label: '查看所有任务',
      api: '',
    } as SchedulerCardAction;
    const actions = [editAction, viewAllAction];
    if (cardStatus === SchedulerTaskCardStatus.RUNNING) {
      actions.unshift({
        type: 'disable',
        label: '暂停',
        api: '/api/mario/scheduler/enabled/{config_id}',
        method: 'PUT',
        params: { config_id: configId, enabled: false },
      } as SchedulerCardAction);
    }
    if (cardStatus === SchedulerTaskCardStatus.PAUSED) {
      actions.unshift({
        type: 'enable',
        label: '恢复',
        api: '/api/mario/scheduler/enabled/{config_id}',
        method: 'PUT',
        params: { config_id: configId, enabled: true },
      } as SchedulerCardAction);
    }
    return actions;
  }, [configId, cardStatus]);

  const notifySideAction = (action: SchedulerCardAction) => {
    if (action.type === 'edit') {
      EventBus.emit(GlobalEventType.SCHEDULER_EDIT_TASK, {
        configId,
        action,
        card: viewPayload,
      });
      return true;
    }
    if (action.type === 'view_all') {
      EventBus.emit(GlobalEventType.SCHEDULER_VIEW_ALL_TASK, {
        // configId,
        // action,
        // card: viewPayload,
      });
      return true;
    }
    return false;
  };

  const onAction = async (action: SchedulerCardAction) => {
    if (notifySideAction(action)) {
      return;
    }
    if (!isAllowedSchedulerAction(action)) {
      Message.error({ message: '操作暂不支持' });
      return;
    }
    try {
      setLoadingType(action.type);
      await executeSchedulerAction(action);
      if (action.type === 'enable') {
        setCardStatus(SchedulerTaskCardStatus.RUNNING);
      }
      if (action.type === 'disable') {
        setCardStatus(SchedulerTaskCardStatus.PAUSED);
      }
      emitConversationEventsRefresh({
        conversationId,
        source: 'scheduler_task_card',
        reason: action.type,
      });
      Message.success({ message: '操作成功' });
    } catch (error) {
      Message.error({
        message: error instanceof Error ? error.message : '操作失败',
      });
    } finally {
      setLoadingType('');
    }
  };

  return (
    <div className="scheduler-task-card">
      <div className="scheduler-task-card-header">
        <img
          src={SchedulerIcon}
          alt="scheduler-icon"
          className="scheduler-task-card-icon"
        />

        <div className="scheduler-task-card-header-main">
          <div className="scheduler-task-card-title-row">
            <div className="scheduler-task-card-title">
              {viewPayload?.title || '定时任务'}
            </div>
            {cardStatusLabel ? (
              <Tag className={`scheduler-task-card-status-tag ${cardStatus}`}>
                {cardStatusLabel}
              </Tag>
            ) : null}
          </div>
          <div className="scheduler-task-card-subtitle">
            定时任务{syncing ? ' · 同步中' : ''}
          </div>
        </div>
        <div className="scheduler-task-card-actions">
          {cardStatus === SchedulerTaskCardStatus.RUNNING ? (
            <Button
              className="scheduler-task-card-actions-btn scheduler-task-card-run-btn"
              onClick={() => onAction(runAction)}
              loading={loadingType === runAction.type}
            >
              {runAction.label || '立即运行'}
            </Button>
          ) : null}
          {cardStatus !== SchedulerTaskCardStatus.DELETED ? (
            <Dropdown
              menu={{
                items: moreActions.map((action) => ({
                  key: action.type,
                  label: action.label,
                  onClick: () => onAction(action),
                })),
              }}
              trigger={['click']}
            >
              <Button
                className="scheduler-task-card-actions-btn scheduler-task-card-more-btn"
                loading={loadingType !== '' && loadingType !== 'run'}
                icon={
                  <MoreOutlined className="scheduler-task-card-more-btn-icon" />
                }
              />
            </Dropdown>
          ) : null}
        </div>
      </div>
      <div className="scheduler-task-card-content">
        <div className="scheduler-task-card-line">
          <span className="scheduler-task-card-line-label">
            {triggerType === 'once' ? '不重复' : '重复'}
          </span>
          <span className="scheduler-task-card-line-value">
            {viewPayload?.trigger_description || ''}
          </span>
        </div>
        <div className="scheduler-task-card-line">
          <span className="scheduler-task-card-line-label">首次执行</span>
          <span className="scheduler-task-card-line-value">
            {formatSchedulerFirstRunTime(viewPayload?.next_run_time)}
          </span>
        </div>
        <div className="scheduler-task-card-detail">
          {viewPayload?.task || '-'}
        </div>
      </div>
    </div>
  );
};
